/**
 * Admin template API service
 *
 * Provides all CRUD operations for the question template management admin UI.
 * Uses fetchWithAuth for all admin endpoints (requires admin JWT).
 * The fetchActiveTemplates function uses raw fetch (public endpoint, no auth needed).
 */

import { fetchWithAuth } from '../utils/fetchWithAuth';

const getBaseUrl = () => {
    return process.env.REACT_APP_FUNCTIONS_URL || 'https://realtranslation-func-dev-eastus2.azurewebsites.net/api';
};

/**
 * Fetch all templates (including inactive) for admin view
 * @returns {Promise<Array>} Array of template documents
 */
const fetchAllTemplates = async () => {
    const url = `${getBaseUrl()}/meeting-ai/templates/admin`;
    const response = await fetchWithAuth(url, { method: 'GET' });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to fetch templates' }));
        throw new Error(errorData.error || `Failed to fetch templates (${response.status})`);
    }

    const data = await response.json();
    return data.templates || [];
};

/**
 * Create a new question template
 * @param {Object} template - Template data to create
 * @returns {Promise<Object>} Created template document
 */
const createTemplate = async (template) => {
    const url = `${getBaseUrl()}/meeting-ai/templates/admin`;
    const response = await fetchWithAuth(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(template),
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to create template' }));
        throw new Error(errorData.error || `Failed to create template (${response.status})`);
    }

    return response.json();
};

/**
 * Update an existing question template
 * @param {string} key - Template key to update
 * @param {Object} template - Updated template data
 * @returns {Promise<Object>} Updated template document
 */
const updateTemplate = async (key, template) => {
    const url = `${getBaseUrl()}/meeting-ai/templates/admin/${encodeURIComponent(key)}`;
    const response = await fetchWithAuth(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(template),
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to update template' }));
        throw new Error(errorData.error || `Failed to update template (${response.status})`);
    }

    return response.json();
};

/**
 * Delete a question template
 * @param {string} key - Template key to delete
 * @returns {Promise<void>}
 */
const deleteTemplate = async (key) => {
    const url = `${getBaseUrl()}/meeting-ai/templates/admin/${encodeURIComponent(key)}`;
    const response = await fetchWithAuth(url, { method: 'DELETE' });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to delete template' }));
        throw new Error(errorData.error || `Failed to delete template (${response.status})`);
    }
};

/**
 * Adjust the rank of a template
 * @param {string} key - Template key
 * @param {number} rank - New rank value
 * @returns {Promise<Object>} Result with success flag and new rank
 */
const adjustTemplateRank = async (key, rank) => {
    const url = `${getBaseUrl()}/meeting-ai/templates/admin/${encodeURIComponent(key)}/rank`;
    const response = await fetchWithAuth(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rank }),
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to adjust rank' }));
        throw new Error(errorData.error || `Failed to adjust rank (${response.status})`);
    }

    return response.json();
};

/**
 * Recalibrate all template rankings from usage data
 * @returns {Promise<Object>} Result with success flag and message
 */
const recalibrateRankings = async () => {
    const url = `${getBaseUrl()}/meeting-ai/templates/admin/recalibrate`;
    const response = await fetchWithAuth(url, { method: 'POST' });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to recalibrate rankings' }));
        throw new Error(errorData.error || `Failed to recalibrate rankings (${response.status})`);
    }

    return response.json();
};

/**
 * Fetch active templates for public preview (no auth required)
 * @param {string} lang - Language code (e.g. 'en', 'ko')
 * @param {number} topN - Number of templates to return
 * @returns {Promise<Array>} Array of active template previews
 */
const fetchActiveTemplates = async (lang = 'en', topN = 5) => {
    const url = `${getBaseUrl()}/meeting-ai/templates?lang=${encodeURIComponent(lang)}&topN=${topN}`;
    const response = await fetch(url);

    if (!response.ok) {
        throw new Error(`Failed to fetch active templates (${response.status})`);
    }

    const data = await response.json();
    return data.templates || [];
};

/**
 * Generate translations for template text to multiple target languages
 * @param {string} text - Source text to translate
 * @param {string[]} targetLanguages - Array of language codes to translate into
 * @returns {Promise<Object>} Object mapping language codes to translated text
 */
const generateTranslations = async (text, targetLanguages) => {
    const url = `${getBaseUrl()}/meeting-ai/templates/admin/generate-translations`;
    const response = await fetchWithAuth(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, sourceLanguage: 'en', targetLanguages }),
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to generate translations' }));
        throw new Error(errorData.error || `Failed to generate translations (${response.status})`);
    }

    const data = await response.json();
    return data.translations || {};
};

const templateAdminService = {
    fetchAllTemplates,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    adjustTemplateRank,
    recalibrateRankings,
    fetchActiveTemplates,
    generateTranslations,
};

export default templateAdminService;
